import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import {
  type PubsubConnectAuthData,
  type StoredPubsubWebsocketIdentity,
} from "shared/types/websocket-auth";

import { createReplaceAllReducer } from "store/utils";
import { logInfoWithDebugFlag } from "utils/logger";

export type AuthState = {
  user_id: string | null;
  collaboration_jwt: string | null;
  websocket: StoredPubsubWebsocketIdentity;
  // This is mainly used to make sure we don't show 404 when we're loading
  // sections that are from a different org, and instead redirect the user.
  // We set this once at startup from the /users/me endpoint which is cross-org.
  section_to_org_id_map: Record<string, string>;
};

const initialState = (): AuthState => ({
  user_id: null,
  collaboration_jwt: null,
  section_to_org_id_map: {},
  websocket: {
    user_id: null,
    org_id: null,
    section_id: null,
    uniqueId: null,
  },
});

export const AuthStore = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Note that setting/clearing the current user will also trigger starting/stopping tracking events in Amplitude
    // if Amplitude is enabled for the current environment.
    setCurrentUser: (state, action: PayloadAction<Omit<AuthState, "websocket"> | null>) => {
      if (!action.payload) {
        state.user_id = null;
        state.collaboration_jwt = null;
        state.section_to_org_id_map = {};
        state.websocket = {
          user_id: null,
          org_id: null,
          section_id: null,
          uniqueId: null,
        };
        return;
      }

      state.user_id = action.payload.user_id;
      state.collaboration_jwt = action.payload.collaboration_jwt;
      state.section_to_org_id_map = action.payload.section_to_org_id_map;
    },
    // Pubsub connect sends identity once; Redis permission sync does not push auth-updates to the browser.
    setWebsocketUserData: (state, action: PayloadAction<PubsubConnectAuthData>) => {
      const { uniqueId, ...newData } = action.payload;
      logInfoWithDebugFlag("debug_pubsub", `Websocket user auth update for (${uniqueId})`, newData);

      state.websocket.user_id = newData.user_id;
      state.websocket.org_id = newData.org_id;
      state.websocket.section_id = newData.section_id;
      state.websocket.uniqueId = uniqueId;
    },
  },
  extraReducers: createReplaceAllReducer<AuthState>("auth", initialState),
});

export const authStateActions = AuthStore.actions;
